// src/components/menus/BeingsPageSubMenu.tsx
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Client, Pet } from '../../services/api';
import { BeingsPageSubMenuButton, BackButton } from '../common/Buttons';
import { SubMenuContainer, SubMenuCap } from './shared/MenuElements';

interface BeingsPageSubMenuProps {
  being: Client | Pet;
  type: 'client' | 'pet';
  onBack?: () => void;
  showBackButton?: boolean;
  bottomBorder?: boolean;
  buttonBottomBorder?: boolean;
  backButtonMode?: 'browser' | 'custom' | 'callback';
  customBackPath?: string;
  backSteps?: number;
}

const isClient = (being: Client | Pet): being is Client => {
  return being !== null && being !== undefined && 'firstName' in being;
};

const BeingsPageSubMenu: React.FC<BeingsPageSubMenuProps> = ({
  being,
  type,
  onBack,
  showBackButton = true,
  bottomBorder = true,
  buttonBottomBorder = false,
  backButtonMode = 'browser',
  customBackPath,
  backSteps = -1,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getBaseUrl = () => {
    if (isClient(being)) {
      return `/clients/${being.id}`;
    } else {
      return `/pets/${being.id}`;
    }
  };

  const getClientId = () => {
    if (isClient(being)) {
      return being.id;
    } else if (being && 'clientId' in being && being.clientId) {
      return being.clientId;
    } else if (being && 'client' in being && being.client) {
      return being.client.id;
    }
    return null;
  };

  const isActiveTab = (tab: string) => {
    const path = location.pathname;

    if (tab === 'clientdetails') {
      if (isClient(being)) {
        const basePattern = `/clients/${being.id}`;
        return path === basePattern || path === `${basePattern}/`;
      } else {
        const basePattern = `/pets/${being.id}`;
        return path === basePattern || path === `${basePattern}/`;
      }
    }

    if (tab === 'pets') {
      return path.includes('/pets') && !path.match(/\/pets\/\d+/);
    }

    if (tab === 'newbooking') {
      return path.includes('/newbooking');
    }

    if (tab === 'newquotation') {
      return path.includes('/quote/new');
    }

    if (tab === 'quotations') {
      return path.includes('/quotations');
    }

    if (tab === 'bookings') {
      return path.includes('/bookings') && !path.includes('/newbooking');
    }

    if (tab === 'newpet') {
      return path.includes('/newpet');
    }

    return path.includes(`/${tab}`);
  };

  const handleClientDetailsClick = () => {
    if (isClient(being)) {
      navigate(`/clients/${being.id}`);
    } else {
      navigate(`/pets/${being.id}`);
    }
  };

  const handleNewBookingClick = () => {
    if (isClient(being)) {
      navigate(`/clients/${being.id}/newbooking`);
    } else {
      const clientId = getClientId();
      if (clientId) {
        navigate(`/clients/${clientId}/newbooking`);
      }
    }
  };

  const handleNewQuotationClick = () => {
    const clientId = isClient(being) ? being.id : getClientId();
    if (clientId) {
      navigate(`/clients/${clientId}/quote/new`);
    }
  };

  const handleQuotationsClick = () => {
    const clientId = isClient(being) ? being.id : getClientId();
    if (clientId) {
      navigate(`/clients/${clientId}/quotations`);
    }
  };

  const handleBookingsClick = () => {
    const baseUrl = getBaseUrl();
    navigate(`${baseUrl}/bookings`);
  };

  const handlePetsClick = () => {
    if (isClient(being)) {
      navigate(`/clients/${being.id}/pets`);
    } else {
      const clientId = getClientId();
      if (clientId) {
        navigate(`/clients/${clientId}/pets`);
      }
    }
  };

  const handleNewPetClick = () => {
    if (isClient(being)) {
      navigate(`/clients/${being.id}/newpet`);
    } else {
      const clientId = getClientId();
      if (clientId) {
        navigate(`/clients/${clientId}/newpet`);
      }
    }
  };

  const handleAccountClick = () => {
    if (isClient(being)) {
      navigate(`/clients/${being.id}/account`);
    } else {
      const clientId = getClientId();
      if (clientId) {
        navigate(`/clients/${clientId}/account`);
      }
    }
  };

  const handleEditClick = () => {
    const baseUrl = getBaseUrl();
    navigate(`${baseUrl}/edit`);
  };

  const handleBackClick = () => {
    if (onBack && backButtonMode === 'callback') {
      onBack();
      return;
    }

    switch (backButtonMode) {
      case 'custom':
        if (customBackPath) {
          navigate(customBackPath);
        } else {
          window.history.go(backSteps);
        }
        break;
      case 'browser':
      default:
        window.history.go(backSteps);
        break;
    }
  };

  const menuItems = [
    {
      id: 'clientdetails',
      label: type === 'client' ? 'CLIENT DETAILS' : 'PET DETAILS',
      onClick: handleClientDetailsClick,
      isActive: isActiveTab('clientdetails'),
      minWidth: type === 'client' ? '266px' : '216px',
    },
    {
      id: 'newbooking',
      label: 'NEW BOOKING',
      onClick: handleNewBookingClick,
      isActive: isActiveTab('newbooking'),
      minWidth: '250px',
    },
    {
      id: 'bookings',
      label: 'BOOKINGS',
      onClick: handleBookingsClick,
      isActive: isActiveTab('bookings'),
      minWidth: '208px',
    },
    {
      id: 'newquotation',
      label: 'NEW QUOTE',
      onClick: handleNewQuotationClick,
      isActive: isActiveTab('newquotation'),
      minWidth: '212px',
    },
    {
      id: 'quotations',
      label: 'QUOTES',
      onClick: handleQuotationsClick,
      isActive: isActiveTab('quotations'),
      minWidth: '154px',
    },
    {
      id: 'newpet',
      label: 'NEW PET',
      onClick: handleNewPetClick,
      isActive: isActiveTab('newpet'),
      minWidth: '176px',
    },
    {
      id: 'pets',
      label: 'PETS',
      onClick: handlePetsClick,
      isActive: isActiveTab('pets'),
      minWidth: '110px',
    },
    {
      id: 'account',
      label: 'ACCOUNT',
      onClick: handleAccountClick,
      isActive: isActiveTab('account'),
      minWidth: '178px',
    },
    {
      id: 'edit',
      label: 'EDIT',
      onClick: handleEditClick,
      isActive: isActiveTab('edit'),
      minWidth: '110px',
    },
  ];

  return (
    <SubMenuContainer bottomBorder={bottomBorder}>
      {menuItems.map((item) => (
        <BeingsPageSubMenuButton
          key={item.id}
          onClick={item.onClick}
          isActive={item.isActive}
          minWidth={item.minWidth}
          showBottomBorder={buttonBottomBorder}
        >
          {item.label}
        </BeingsPageSubMenuButton>
      ))}

      {showBackButton && (
        <BackButton
          onClick={handleBackClick}
          navigationMode={
            backButtonMode === 'callback' ? 'custom' : backButtonMode
          }
          customPath={customBackPath}
          steps={backSteps}
        />
      )}
      <SubMenuCap />
    </SubMenuContainer>
  );
};

export default BeingsPageSubMenu;
